import { APPEXCHANGE_URLS, BUCKETS } from '~/modules/config';
import { EXPLORE_BASE_ROUTE, LEARN_BASE_ROUTE } from '~/modules/routes';

const EXPLORE_URLS = Object.values(BUCKETS).map((bucket) => `/${EXPLORE_BASE_ROUTE}/${bucket}`);

const baseUrlRegex = `(${APPEXCHANGE_URLS.map((base) => base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`;

export enum SponsoredUrlIdentifier {
    SPONSORED_SEARCH = 'sponsored-search',
    SPONSORED_EXPLORE = 'sponsored-explore',
}

export const SPONSORED_SEARCH_REGEX = new RegExp(`channel=${SponsoredUrlIdentifier.SPONSORED_SEARCH}`);
export const SPONSORED_EXPLORE_REGEX = new RegExp(`channel=${SponsoredUrlIdentifier.SPONSORED_EXPLORE}`);

export const isExploreBucketUrl = (url: string) => {
    return url && EXPLORE_URLS.some((exploreUrl) => url.includes(exploreUrl));
};

export const hasKeyword = (url: string) => {
    return /keywords=[^&]+/.test(url);
};

export const isAppExchange = (url: string) => {
    const expression = new RegExp(`^https?://.*${baseUrlRegex}`);
    return expression.test(url);
};

export const urlIsSearchPage = (url: string) => {
    return /\/appxSearchKeywordResults/.test(url);
};

export const urlIsKeywordlessSearch = (url: string) => {
    return urlIsSearchPage(url) && !hasKeyword(url);
};

export const isKeywordlessSearch = (url: string, referrer: string) => {
    return urlIsKeywordlessSearch(url) || (!urlIsSearchPage(url) && urlIsKeywordlessSearch(referrer));
};

export const urlIsGoogle = (url: string) => {
    return url && /google/.test(new URL(url).hostname);
};

export const urlContainsGoogleParameter = (url: string) => {
    return /gclid=/.test(url);
};

export const extractURLParameterValue = (url: string, param: string): string | null | undefined => {
    if (!url) {
        return undefined;
    }

    return new URL(url).searchParams.get(param);
};

export const urlContainsFacebookParameter = (url: string) => {
    const socParam = extractURLParameterValue(url, 'soc');
    return (socParam && /fb/.test(socParam)) ?? false;
};

export const urlIsFacebook = (url: string) => {
    return /^https?:\/\/(\w+\.)?facebook\.com/.test(url);
};

const hasDomainName = (domains: string[], url: string): boolean => {
    if (!url) {
        return false;
    }
    return domains.some((domain) => domain === new URL(url).host.replace('www.', ''));
};

export const urlIsLinkedIn = (url: string) => {
    const linkedInDomainNames = ['linkedin.com', 'lnkd.in'];
    return hasDomainName(linkedInDomainNames, url);
};

export const urlContainsLinkedInParameter = (url: string) => {
    const utmSourceParamValue = extractURLParameterValue(url, 'utm_source');
    return (utmSourceParamValue && /linkedin/.test(utmSourceParamValue)) ?? false;
};

export const urlIsTwitter = (url: string) => {
    const twitterDomainNames = ['twitter.com', 'x.com', 't.co'];
    return hasDomainName(twitterDomainNames, url);
};

export const urlContainsTwitterParameter = (url: string) => {
    const utmSourceParamValue = extractURLParameterValue(url, 'utm_source');
    return (utmSourceParamValue && /twitter/.test(utmSourceParamValue)) ?? false;
};

export const isLearn = (url: string) => {
    return url.includes(`/${LEARN_BASE_ROUTE}`);
};
